import { DialogsRenderer } from '../components/widgets/dialog';
import { NotificationToasterRenderer } from '../components/widgets/notificationToaster';
import React, { FC, FunctionComponent, Suspense, useEffect, useState } from 'react';
import { graphql, useFragment } from 'react-relay/hooks';
import { useLocation } from 'react-router-dom';
import Usetiful from 'src/shared/usetiful';
import styled from 'styled-components';
import ErrorPageBoundary from '../components/pages/_error';
import OverlayLoading from '../components/widgets/overlayLoading';
import CreditsAlert from '../components/widgets/toolbar/creditsAlert';
import { setLastUsedTool as setLastUsedToolAction, setPriceFormatter as setPriceFormatterAction } from '../shared/actions';
import { useAppState } from '../shared/appState';
import config from '../shared/config';
import { FeatureFlagsProvider } from '../shared/feature-flags';
import { scrollbarsStyle } from '../shared/globals';
import { backofficeUrl, scrollSource } from '../shared/helpers';
import { useAccountData } from '../shared/hooks/useAccountData';
import ProfitwellComponent from '../shared/profitWell';
import TagManager from '../shared/tagManager';
import { defaultLayoutContainerPriceFormatterSupplierFragment$key } from './__generated__/defaultLayoutContainerPriceFormatterSupplierFragment.graphql';
import { defaultLayoutContainerSellerLastUsedToolFragment$key } from './__generated__/defaultLayoutContainerSellerLastUsedToolFragment.graphql';

const Container = styled.div`
    font-size: 14px;
    font-family: ${(props) => props.theme.app.font};
    color: ${(props) => props.theme.app.primary};
    min-height: 100%;
    padding: 0px;

    ${scrollbarsStyle}
`;

const preventScrollTopPreviousRoutes = [
    { from: backofficeUrl('/product/variations/list/'), to: backofficeUrl('/product/variations/list/print-area-editor') },
    { from: backofficeUrl('/product/variations/list/print-area-editor'), to: backofficeUrl('/product/variations/list/') },
    { from: backofficeUrl('/product/print-area/'), to: backofficeUrl('/product/print-area/print-area-editor') },
    { from: backofficeUrl('/product/print-area/print-area-editor'), to: backofficeUrl('/product/print-area/') }
];

const priceFormatterSupplierFragment = graphql`
    fragment defaultLayoutContainerPriceFormatterSupplierFragment on Query {
        ecommerceSettings {
            currencyCode
        }
    }
`;
const sellerLastUsedToolFragment = graphql`
    fragment defaultLayoutContainerSellerLastUsedToolFragment on Query {
        sellerLastUsedTool
    }
`;

const ScrollTop: FC<{ children?: React.ReactNode }> = ({ children }) => {
    const { pathname } = useLocation();
    const [previousPathname, setPreviousPathname] = useState<string>(pathname);

    useEffect(() => {
        if (preventScrollTopPreviousRoutes.findIndex((x) => x.from === previousPathname && x.to === pathname) === -1) {
            scrollSource.scrollTo(0, 0);
        }

        setPreviousPathname(pathname);
    }, [pathname]);

    return <React.Fragment>{children}</React.Fragment>;
};

export type AdditionalInfo = {
    noValidSubscriptionReason?: string;
    subscriptionCancel?: string;
    isSubscriptionSubStatusBlocked?: boolean;
};

export type UserInfo = {
    email: string;
    name: string;
    userID: number;
};

export type SellerStaticsDetail = {
    userID: number;
    state?: number;
    subState?: number;
    username?: string;
    registeredOn?: string;
    subscribedOn?: string;
    configuredOn?: string;
    planName?: string;
    orders?: number;
    ordersAsPaying?: number;
    quantity?: number;
    amount?: number;
    interactions?: number;
    products?: number;
    designs?: number;
    printfulProducts?: number;
    printfulOrders?: number;
    lastDayOrders?: number;
    lastOrderDate?: string;
    totalFees?: number;
    previousDayItemsOrdered?: number;
    userSource?: number;
    privacy?: boolean;
    isExtraViews?: boolean;
    identityProviderId?: number;
    integrationState?: number;
    eCommerceURL?: string;
    subscriptionID?: number;
    lastLogin?: string;
    eCommerceCurrencyID?: number;
};

const PriceFormatterSupplier: FC<{}> = () => {
    const { dispatch, queryData } = useAppState();

    const data = useFragment<defaultLayoutContainerPriceFormatterSupplierFragment$key>(
        priceFormatterSupplierFragment,
        queryData?.defaultLayoutContainerPriceFormatterSupplierFragment ?? null
    );

    useEffect(() => {
        if (!data) return;

        const priceFormatter = new Intl.NumberFormat(config.culture, {
            style: 'currency',
            currency: data.ecommerceSettings?.currencyCode || 'USD'
        });
        dispatch(setPriceFormatterAction(priceFormatter));
    }, [data?.ecommerceSettings?.currencyCode]);

    return null;
};
const LastToolProvider: FC<{}> = () => {
    const { dispatch, queryData } = useAppState();

    // Extract lastUsedTool from the query
    const data = useFragment<defaultLayoutContainerSellerLastUsedToolFragment$key>(
        sellerLastUsedToolFragment,
        queryData?.defaultLayoutContainerSellerLastUsedToolFragment ?? null
    );
    const lastUsedTool = data?.sellerLastUsedTool;

    useEffect(() => {
        if (lastUsedTool != null) {
            dispatch(setLastUsedToolAction(lastUsedTool));
        }
    }, [lastUsedTool]);

    return null;
};

const AppScriptContainerProvider: FunctionComponent<{}> = () => {
    const { userInfo, ecommerceSettings, sellerStaticsDetail } = useAccountData();

    return (
        <>
            <ProfitwellComponent email={userInfo?.email ?? ''} />
            <Usetiful
                userID={userInfo.userID ?? -1}
                eCommerceID={ecommerceSettings?.ecommerceID ?? 0}
                eCommerceName={ecommerceSettings?.ecommerceName ?? 'none'}
                sellerStaticsDetail={sellerStaticsDetail as SellerStaticsDetail}
            />
            {config.isLiveTagManager && (
                <TagManager
                    userInfo={userInfo}
                    eCommerceName={ecommerceSettings?.ecommerceName ?? 'none'}
                    sellerStaticsDetail={sellerStaticsDetail as SellerStaticsDetail}
                    token={!!config.token}
                />
            )}
        </>
    );
};

// Feature flags wrapper that provides user context
const FeatureFlagsWrapper: FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
    const { userInfo } = useAccountData();

    const initialUserContext = React.useMemo(
        () => ({
            userId: userInfo?.userID?.toString() || undefined,
            userEmail: userInfo?.email || undefined,
            userName: userInfo?.name || undefined
        }),
        [userInfo]
    );

    return <FeatureFlagsProvider initialUserContext={initialUserContext}>{children}</FeatureFlagsProvider>;
};

export const DefaultLayoutContainer: FunctionComponent<{ className?: string; children?: React.ReactNode }> = ({ className, children }) => {
    return (
        <>
            <Suspense fallback={null}>
                <AppScriptContainerProvider />
            </Suspense>
            <FeatureFlagsWrapper>
                <Suspense fallback={<OverlayLoading forceShow />}>
                    <CreditsAlert />
                    <NotificationToasterRenderer />
                    <Container className={className}>
                        <ErrorPageBoundary>
                            <ScrollTop>
                                {children}
                                <DialogsRenderer />
                                <OverlayLoading />
                                <PriceFormatterSupplier />
                                <LastToolProvider />
                            </ScrollTop>
                        </ErrorPageBoundary>
                    </Container>
                </Suspense>
            </FeatureFlagsWrapper>
        </>
    );
};
